"use client";

import { useSyncExternalStore } from "react";

// Tailwind lg 브레이크포인트(사이드바·AboveLike 광고 노출 기준)
const LG_BREAKPOINT = 1024;
// 컴팩트 셸 상한(JCG-2224): 1024~1279px 구간에선 셸을 유지하되, 좁은 가로폭에서 본문이
// 과도하게 압축되지 않도록 사이드바가 본문에 폭을 양보한다(좌측 레일 자동 접힘 + 우측 폭 캡).
// 1280px 이상(와이드 데스크탑)은 기존 경험 그대로.
const SHELL_COMPACT_MAX = 1280;

function subscribeWindowResize(callback: () => void) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("resize", callback);
  return () => window.removeEventListener("resize", callback);
}

const getIsDesktopSnapshot = () =>
  typeof window !== "undefined" && window.innerWidth >= LG_BREAKPOINT;

// SSR/hydration 시점에는 false로 고정해 서버 HTML과 클라이언트 첫 렌더를 일치시킨다.
// (useSyncExternalStore가 hydration 동안 getServerSnapshot을 사용하므로 mismatch 없음, JCG-2029 패턴)
const getServerFalseSnapshot = () => false;

/**
 * Tailwind lg(≥1024px) 기준 데스크톱 뷰포트 여부.
 *
 * AdSense ins는 CSS로 숨기면 잘못된 뷰포트에서도 DOM에 남아 adsbygoogle의
 * 위치(positional) 기반 큐를 오염시킨다. 이 훅으로 조건부 렌더하여 현재 뷰포트에
 * 맞는 ins만 DOM에 두기 위해 사용한다(JCG-2057).
 */
export function useIsDesktop(): boolean {
  return useSyncExternalStore(
    subscribeWindowResize,
    getIsDesktopSnapshot,
    getServerFalseSnapshot,
  );
}

// 셸 게이트 전용 서버 스냅샷 — true.
const getServerTrueSnapshot = () => true;

/**
 * 셸 게이트 전용 데스크톱(≥1024px) 여부 — `useIsDesktop()` 과 달리 **서버 스냅샷이 true**.
 *
 * UI 2.0 셸(글 페이지 ArticleShell·커뮤니티 셸)은 SSR 에서부터 데스크탑 레이아웃(상단 NAV 제거)을
 * 그려야 하므로 서버 기본값을 true 로 둔다. 모바일 클라이언트는 hydration 후 클래식으로 전환되며
 * `useSyncExternalStore` 가 getServerSnapshot 으로 첫 렌더를 일치시켜 mismatch 가 없다.
 * 셸 분기를 결정하는 `LayoutSwitch` 와 보드 레이아웃을 그리는 `CommunityClient` 가 **반드시 같은
 * 판정**을 써야 더블 크롬·NAV 소실이 생기지 않으므로 이 단일 훅을 공유한다. (JCG-2106)
 */
export function useIsDesktopShell(): boolean {
  return useSyncExternalStore(
    subscribeWindowResize,
    getIsDesktopSnapshot,
    getServerTrueSnapshot,
  );
}

// 컴팩트 셸 구간(1024~1279px) 스냅샷 — 셸 게이트(≥1024)는 유지되는 그 안쪽 구간만 true.
const getIsCompactShellSnapshot = () =>
  typeof window !== "undefined" &&
  window.innerWidth >= LG_BREAKPOINT &&
  window.innerWidth < SHELL_COMPACT_MAX;

/**
 * 컴팩트 셸 구간(1024~1279px) 여부 (JCG-2224).
 *
 * 셸 게이트(`useIsDesktopShell()`, ≥1024)는 그대로 유지하면서, 태블릿 가로·좁은 데스크탑
 * 구간에서만 셸 내부에서 사이드바가 본문에 폭을 양보하도록 분기하는 전용 훅이다(좌측을 레일로
 * 자동 접고 우측 폭을 캡해 중앙 본문 폭을 확보). 서버 스냅샷은 **false** — SSR/와이드 데스크탑을
 * 기본으로 두어 1280px↑ 경험을 건드리지 않고, 모바일/태블릿 클라이언트는 hydration 후 판정한다
 * (`useSyncExternalStore` 가 getServerSnapshot 으로 첫 렌더를 일치시켜 mismatch 없음).
 */
export function useIsCompactShell(): boolean {
  return useSyncExternalStore(
    subscribeWindowResize,
    getIsCompactShellSnapshot,
    getServerFalseSnapshot,
  );
}
